"use client";

import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/**
 * The WAI-ARIA dialog behaviour contract, extracted from `PromptDialog` (see
 * `components/ui/confirm-dialog.tsx`) so every modal surface can adopt it
 * without re-implementing it. This is the "not up for design" accessibility
 * baseline from `docs/design/MODAL-SURFACE-BRIEF.md` and issue #1721:
 *
 *  - **Escape closes.** Handled in the CAPTURE phase and `stopPropagation`'d, so
 *    a dialog opened OVER another modal (e.g. the mobile project-menu sheet,
 *    which runs its own document-level Escape handler) closes THIS dialog only
 *    — without it, one press closed the dialog AND the sheet underneath it.
 *  - **Focus moves INTO the dialog on open.** The default is the first focusable
 *    descendant, falling back to the container itself; pass `initialFocus` to
 *    override. rAF-deferred so it wins over any child `autoFocus`.
 *  - **Focus is trapped** while open — Tab cycles between the first and last
 *    focusable descendant instead of escaping into the obscured page behind.
 *  - **Focus returns** to a surviving element on close. Defaults to whatever
 *    held focus when the dialog opened; pass `returnFocusTo` (a lazy getter,
 *    read at close time) for a caller that dismisses its own menu to open the
 *    dialog and so has nothing left to restore to.
 *
 * Attach the returned ref to the dialog PANEL (the element carrying
 * `role="dialog"` / `role="alertdialog"`), NOT the backdrop. Render `null` while
 * closed as usual — the effect cleanup returns focus when the panel unmounts.
 *
 * `components/ui/sheet.tsx` remains the reference for the fuller contract
 * (scroll lock, nested-dialog inertness); this hook covers the semantics-and-
 * focus baseline that every dialog needs.
 */
export interface DialogA11yOptions {
  open: boolean;
  /** Close request — fired on Escape. */
  onClose: () => void;
  /**
   * Element to focus when the dialog opens. Defaults to the first focusable
   * descendant of the panel, falling back to the panel itself.
   */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * Focus the panel CONTAINER itself on open instead of the first focusable
   * descendant. Use when a keypress bound at the document level would clash with
   * the first control's native activation — e.g. an alertdialog whose Enter
   * confirms would otherwise also activate the focused Cancel button. The
   * container still announces because it carries the accessible name.
   * Ignored when `initialFocus` is provided.
   */
  focusContainer?: boolean;
  /**
   * Where to send focus when the dialog closes. Resolved lazily (a getter, not
   * an element) so it reads the ref at close time. Falls back to whatever held
   * focus when the dialog opened.
   *
   * A caller that dismisses its own menu to open this dialog MUST supply one:
   * the two state updates batch into a single commit, so the control that had
   * focus is already unmounted by the time this hook runs and
   * `document.activeElement` has fallen back to `<body>`.
   */
  returnFocusTo?: () => HTMLElement | null;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Is this element genuinely reachable by Tab, right now?
 *
 * The selector alone is not enough. It matched `class="hidden"` controls —
 * `display: none` applied by a CSS class, which no attribute check can see —
 * and an earlier revision filtered only the HTML `hidden` ATTRIBUTE. The COSHH
 * SDS dialog carries exactly that shape: a `class="hidden"` file input behind a
 * styled label. It counted as the trap's LAST element, so Tab from the only
 * visible control never wrapped, the browser skipped the unfocusable input, and
 * focus escaped into the page behind. (Codex, PR #1760.)
 */
function isTabbable(el: HTMLElement): boolean {
  if (el.hasAttribute("hidden")) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if ((el as HTMLInputElement).disabled) return false;
  if (el.tabIndex < 0) return false;

  // A rendered element has at least one client rect; `display: none` has none.
  // This is the check that catches CSS-driven hiding.
  if (typeof el.getClientRects === "function" && el.getClientRects().length > 0) {
    return true;
  }

  // jsdom performs no layout, so getClientRects() is empty for EVERY element
  // and the check above can never pass there. Fall back to computed style so
  // the behaviour stays testable (via an inline style — jsdom does not apply
  // Tailwind classes). In a real browser this is only reached by elements that
  // genuinely have no box.
  const style = el.ownerDocument?.defaultView?.getComputedStyle?.(el);
  if (style && (style.display === "none" || style.visibility === "hidden")) {
    return false;
  }
  return true;
}

/**
 * Open dialogs, oldest first. Only the TOP entry responds to Escape or traps
 * Tab; the ones underneath stay registered but inert until it closes.
 *
 * This is required because the Escape handler runs in the CAPTURE phase on
 * `document`. That correctly beats a bubble-phase handler underneath (which is
 * how `components/ui/sheet.tsx` stays open when a dialog over it takes an
 * Escape), but `stopPropagation` does NOT stop other listeners on the SAME
 * element in the SAME phase — so without this stack, one Escape would close
 * every hook-driven dialog in a nested pair at once. Real pairs today:
 * `first-doc-celebration`, `share-dialog` and `version-sidebar` each render
 * `IssueConfirmationDialog` over themselves.
 */
const openDialogs: symbol[] = [];

export function useDialogA11y<T extends HTMLElement = HTMLElement>({
  open,
  onClose,
  initialFocus,
  focusContainer,
  returnFocusTo,
}: DialogA11yOptions): RefObject<T | null> {
  const containerRef = useRef<T>(null);
  // Stable identity for this dialog's slot in the nesting stack. Lazy useState
  // rather than a ref so it needs no cast and is never written during render;
  // it is initialised once and never set again, so it costs no re-renders.
  const [dialogId] = useState(() => Symbol("dialog"));

  // Latest option values without re-running the effect (which would steal focus
  // back to the initial element) when callers pass fresh inline arrows/refs
  // each render. Mirrors the ref-latch pattern in components/ui/sheet.tsx.
  const optsRef = useRef({ onClose, initialFocus, focusContainer, returnFocusTo });
  useEffect(() => {
    optsRef.current = { onClose, initialFocus, focusContainer, returnFocusTo };
  });

  useEffect(() => {
    if (!open) return;
    const node = containerRef.current;
    const id = dialogId;
    const captured =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    openDialogs.push(id);
    const isTopmost = () => openDialogs[openDialogs.length - 1] === id;

    // Move focus INTO the dialog. rAF so it wins over any child autoFocus and
    // runs after the panel has painted.
    const raf = requestAnimationFrame(() => {
      const explicit = optsRef.current.initialFocus?.current;
      if (explicit) {
        explicit.focus();
        return;
      }
      if (optsRef.current.focusContainer) {
        node?.focus();
        return;
      }
      const first = node?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (first) first.focus();
      else node?.focus();
    });

    const onKey = (e: KeyboardEvent) => {
      // A dialog underneath an open nested dialog stays registered but inert.
      if (!isTopmost()) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        optsRef.current.onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const el = containerRef.current;
      if (!el) return;
      const focusable = Array.from(
        el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(isTabbable);
      if (focusable.length === 0) {
        // Every control is hidden or disabled — the SDS upload dialog mid-
        // upload disables both its close button and its file input. Returning
        // here let Tab walk straight into the obscured page, so hold focus on
        // the panel instead. Every adopter puts tabIndex={-1} on the panel so
        // this lands. (Codex, PR #1760.)
        e.preventDefault();
        e.stopPropagation();
        el.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      const outside = !el.contains(active);
      if (e.shiftKey && (active === first || outside)) {
        e.preventDefault();
        e.stopPropagation();
        last.focus();
      } else if (!e.shiftKey && (active === last || outside)) {
        e.preventDefault();
        e.stopPropagation();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey, true);
      // Remove by identity, not `pop()` — dialogs do not always close in the
      // order they opened (a route change can unmount an outer one first).
      const at = openDialogs.lastIndexOf(id);
      if (at !== -1) openDialogs.splice(at, 1);
      const explicit = optsRef.current.returnFocusTo?.() ?? null;
      const target = explicit?.isConnected ? explicit : captured;
      if (target?.isConnected && typeof target.focus === "function") {
        target.focus();
      }
    };
    // `dialogId` is lazily initialised once and never set again, so listing it
    // cannot re-run this effect (which would steal focus back to the initial
    // element). It is here only to satisfy exhaustive-deps honestly rather
    // than with a suppression comment.
  }, [open, dialogId]);

  return containerRef;
}
